<template>
  <DraftMain
    :panelTitle="panelTitle"
    :initialValues="initialValues"
    :confirmDeletionFn="confirmDeletionFn"
    :closeModal="closeModal"
    :onSearch="onSearch"
    :showModal="showModal"
  >
    <template #searchHeadOperation>
      <div class="flex items-center justify-center gap-2"></div>
    </template>
    <template #searchOperation="{ resetForm, values }">
      <div class="flex gap-2">
        <FilledButton
          class="px-5 py-1.5 text-white"
          v-if="enablePrint"
          @click.prevent.stop="print(values)"
        >
          <i class="fa fa-file-excel-o"></i>
          {{ $t('print') }}
        </FilledButton>
        <FilledButton class="px-5 py-1.5 text-white">
          <i class="fa fa-search"></i>
          {{ $t('search') }}
        </FilledButton>
      </div>
    </template>
    <template #searchPanel="{ values }">
      <PaymentPanelSearch
        :initialValues="initialValues"
        isShowFingerprintCheckbox="true"
        :values="values"
      />
    </template>
    <template #searchResult>
      <DraftTable
        :is-show-lock-feature="false"
        :isLoading="isLoading"
        :tableData="tableData"
        :tableHeaders="tableHeaders"
        :initialValues="initialValues"
        :pageLength="pageLength"
        @onPageClick="onPageClick"
        @selected="selectedNumberOfRow"
        @checked="selectedRow"
        @checkOut="checkOut"
        @checkIn="checkIn"
        @delete="deleteSelected"
        class="overflow-x-auto"
      >
        <template #tableRow="{ request }">
          <td
            class="px-6 py-2 text-gray-700 text-sm font-normal border-r border-brand-gray-300 text-wrap"
          >
            {{ request.requestNumber }}
          </td>
          <td
            class="px-6 py-2 text-gray-700 text-sm font-normal border-r border-brand-gray-300 text-wrap"
          >
            {{ request.receiptNumber }}
          </td>
          <td
            class="px-6 py-2 text-gray-700 text-sm font-normal border-r border-brand-gray-300 text-wrap"
          >
            {{ request.paymentRefId }}
          </td>
          <td
            class="px-6 py-2 text-gray-700 text-sm font-normal border-r border-brand-gray-300 text-wrap"
          >
            <div v-if="request.paymentMethodId != 6">
              {{ request.amount + request.totalVatAmount }}
            </div>
            <div v-else>
              {{ $t('exemptedFee') }}
            </div>
          </td>
          <td
            class="px-6 py-2 text-gray-700 text-sm font-normal border-r border-brand-gray-300 text-wrap"
          >
            {{ request.paymentDate }}
          </td>
          <td
            class="px-6 py-2 text-gray-700 text-sm font-normal border-r border-brand-gray-300 text-wrap"
          >
            {{ request.paymentMethod?.text }}
          </td>
          <td
            class="px-6 py-2 text-gray-700 text-sm font-normal border-r border-brand-gray-300 text-wrap"
          >
            {{ request.createdByUserName }}
          </td>
          <td
            class="px-6 py-2 text-gray-700 text-sm font-normal border-r border-brand-gray-300 text-wrap"
          >
            {{ request.paymentStatus?.text }}
          </td>
        </template>

        <template #tableOperation="{ request }">
          <button
            class="bg-primary size-6 rounded-md text-white cursor-pointer"
            @click.prevent="goToPaymentDetails(request)"
          >
            <i class="fa fa-credit-card"></i>
          </button>
        </template>
      </DraftTable>
    </template>
  </DraftMain>
</template>
<script setup>
import { onBeforeMount, onBeforeUnmount, ref, watch } from 'vue'
import { notify } from 'notiwind'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { DateTime } from 'luxon'
import DraftMain from '@/views/individual/draft/Main.vue'
import DraftTable from '@/views/individual/draft/DraftTable.vue'
import PaymentPanelSearch from '@/views/individual/draft/PaymentPanelSearch.vue'
import FilledButton from '@/components/Buttons/Filled.vue'
import apiHelperService from '@/services'
import { useAuthStore } from '@/stores/auth'
import qs from 'qs'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const currentSearchValues = ref()

const i18n = useI18n({
  messages: {
    en: {
      requestNumber: 'Request number',
      amount: 'Amount(AED)',
      date: 'Date',
      method: 'Method',
      user: 'User'
    },
    ar: {
      requestNumber: 'رقم الطلب',
      amount: 'المبلغ (درهم إماراتي)',
      date: 'التاريخ',
      method: 'طريقة الدفع',
      user: 'المستخدم'
    }
  }
})

const draftId = ref([])
const showModal = ref(false)
const panelTitle = ref('payments')
const isLoading = ref(false)
const enablePrint = ref(false)
const initialValues = ref({
  startDate: '',
  endDate: '',
  currentPage: 1,
  pageSize: 10
})

const pageLength = ref(10)
const tableData = ref([])
const tableHeaders = ref([
  {
    value: 1,
    label: ''
  },
  {
    value: 2,
    label: i18n.t('requestNumber')
  },
  {
    value: 3,
    label: i18n.t('receiptNumber')
  },
  {
    value: 4,
    label: i18n.t('bankRefernaceNumber')
  },
  {
    value: 5,
    label: i18n.t('amount')
  },
  {
    value: 6,
    label: i18n.t('date')
  },
  {
    value: 7,
    label: i18n.t('method')
  },
  {
    value: 8,
    label: i18n.t('user')
  },
  {
    value: 9,
    label: i18n.t('status')
  }
])

const goToPaymentDetails = (request) => {
  router.push(`/payment-details/${request.requestId}`)
}

const closeModal = () => {
  showModal.value = false
}

const _base64ToArrayBuffer = function (base64) {
  var binary_string = window.atob(base64)
  var len = binary_string.length
  var bytes = new Uint8Array(len)
  for (var i = 0; i < len; i++) {
    bytes[i] = binary_string.charCodeAt(i)
  }
  return bytes.buffer
}

const print = async (values) => {
  const data = {
    pageSize: 10,
    currentPage: 1,
    requestNumber: values.requestNumber,
    receiptNumber: values.receiptNumber,
    startDate: values.startDate,
    endDate: values.endDate,
    paymentMethodId: values.paymentMethodId,
    paymentRefId: values.paymentRefId,
    establishmentId: 0,
    servicesTransactions: []
  }
  const response = await apiHelperService.post('/api/printReport/PrintEstablishmentPayments', data)

  const bufferData = _base64ToArrayBuffer(response.content)
  const blob = new Blob([bufferData], {
    type: 'application/octet-stream'
  })

  const link = document.createElement('a')
  link.setAttribute('type', 'hidden')
  document.body.appendChild(link)
  link.href = window.URL.createObjectURL(blob)

  const fileName = response.reportName + '.xlsx'

  link.download = fileName
  link.click()
  window.URL.revokeObjectURL(link.href)
  document.body.removeChild(link)
}

const requestTableData = async (value) => {
  isLoading.value = true
  const params = {
    ...value,
    pageSize: pageLength.value,
    establishmentId: 0
  }
  currentSearchValues.value = value

  router.replace({ query: value })
  try {
    const response = await apiHelperService.get('/api/payment/client/search', {
      params,
      paramsSerializer: (params) => {
        return qs.stringify(params, { arrayFormat: 'repeat' })
      }
    })
    if (response) {
      isLoading.value = false
      return response
    }
  } catch (error) {
    console.error('requestTableData', error)
  }
}

const selectedNumberOfRow = async (num) => {
  let searchParams
  pageLength.value = num
  if (currentSearchValues.value) {
    searchParams = {
      ...currentSearchValues.value,
      pageSize: +pageLength.value,
      currentPage: 1
    }
  } else {
    searchParams = {
      ...initialValues.value,
      pageSize: +pageLength.value,
      currentPage: 1
    }
  }
  tableData.value = await requestTableData(searchParams)
}

const onPageClick = async (pageNum) => {
  let searchParams
  if (currentSearchValues.value) {
    searchParams = {
      ...currentSearchValues.value,
      currentPage: +pageNum
    }
  } else {
    searchParams = {
      ...initialValues.value,
      currentPage: +pageNum
    }
  }

  tableData.value = await requestTableData(searchParams)
}

const onSearch = async (value) => {
  if (value.startDate && value.endDate) {
    const EndDay = DateTime.fromFormat(value.endDate, 'yyyy/MM/dd')
    const StartDay = DateTime.fromFormat(value.startDate, 'yyyy/MM/dd')
    const diffInDays = EndDay.diff(StartDay, 'days').days
    if (diffInDays > 1) {
      notify(
        {
          group: 'error',
          text: i18n.t('fillvalidDateRangeByDays', { num: 1 })
        },
        6000
      )
      return
    }
  }
  tableData.value = await requestTableData(value)
}

const selectedRow = (draft) => {
  if (!draft) return
  draftId.value = draft
}

const deleteSelected = (draft) => {
  draftId.value = draft
  showModal.value = true
}

const checkOut = async () => {
  let searchParams = currentSearchValues.value || initialValues.value
  tableData.value = await requestTableData(initialValues.value)
  notify(
    {
      group: 'success',
      title: i18n.t('Success'),
      text: i18n.t('checkedOutSuccessfully')
    },
    6000
  )
}

const checkIn = async () => {
  let searchParams = currentSearchValues.value || initialValues.value
  tableData.value = await requestTableData(initialValues.value)
  notify(
    {
      group: 'success',
      title: i18n.t('Success'),
      text: i18n.t('checkedInSuccessfully')
    },
    6000
  )
}

const confirmDeletionFn = async () => {
  try {
    const payload = draftId.value
    const response = await apiHelperService.post('/api/draft/delete', payload)
    if (response) {
      let searchParams = currentSearchValues.value || initialValues.value
      tableData.value = await requestTableData(searchParams)
      showModal.value = false
      notify(
        {
          group: 'success',
          title: i18n.t('Success'),
          text: i18n.t('DeletedSuccessfully')
        },
        6000
      )
    }
  } catch (error) {
    console.error('confirmDeletionFn', error)
  }
}

const setDate = () => {
  let newEndDate = DateTime.now()
  let newStartDate = newEndDate.minus({ days: 3 })

  let formattedEndDate = newEndDate.toFormat('yyyy/MM/dd')
  let formattedStartDate = newStartDate.toFormat('yyyy/MM/dd')
  initialValues.value.endDate = formattedEndDate
  initialValues.value.startDate = formattedStartDate
}

const setDraftResponse = async () => {
  if (!initialValues.value.startDate && !initialValues.value.endDate) {
    setDate()
  }
  tableData.value = await requestTableData(initialValues.value)
}

onBeforeMount(async () => {
  enablePrint.value = authStore.isUserTypingCenter || authStore.isUserEstablishment
  await setDraftResponse()
  const searchParams = route.query
  initialValues.value = searchParams
})
</script>
<style></style>
